import type { LocationDef, PlayerStats, EquipmentInstance, PropertyInstance } from '../types/game';
import { getBoatTierName } from './equipment';

export const LOCATION_DEFS: LocationDef[] = [
  {
    id: 'home',
    name: 'Your House',
    description: 'A modest cottage by the shore. A place to rest and plan your next adventure.',
    type: 'house',
    reveals: ['pond', 'market', 'harbor'],
    unlocks: null,
    fishModifier: 0,
    fishTier: -1,
    bycatchChance: 0,
    requirements: {},
  },
  {
    id: 'dock',
    name: 'Your Dock',
    description: 'Your private berth. Upgrade it to house more vessels.',
    type: 'dock',
    reveals: [],
    unlocks: null,
    fishModifier: 0,
    fishTier: -1,
    bycatchChance: 0,
    requirements: { properties: [{ propertyId: 'dock', tier: 1 }] },
  },
  {
    id: 'pond',
    name: 'Village Pond',
    description: 'A calm, murky pond on the edge of town. Good for beginners.',
    type: 'fishing',
    reveals: ['river'],
    unlocks: null,
    fishModifier: 1.0,
    fishTier: 0,
    bycatchChance: 0.20,
    requirements: {},
  },
  {
    id: 'market',
    name: 'Harbor Market',
    description: 'A bustling marketplace by the docks. Sell your catch and upgrade equipment here.',
    type: 'market',
    reveals: ['coast'],
    unlocks: null,
    fishModifier: 0,
    fishTier: -1,
    bycatchChance: 0,
    requirements: {},
  },
  {
    id: 'harbor',
    name: 'Shipyard Harbor',
    description: 'A working harbor where vessels are built and berthed. The air smells of salt and fresh timber.',
    type: 'harbor',
    reveals: [],
    unlocks: null,
    fishModifier: 0,
    fishTier: -1,
    bycatchChance: 0,
    requirements: {},
  },
  {
    id: 'river',
    name: 'Forest River',
    description: 'A rushing river cutting through ancient woods. The current hides bigger fish.',
    type: 'fishing',
    reveals: ['lake'],
    unlocks: null,
    fishModifier: 0.92,
    fishTier: 1,
    bycatchChance: 0.05,
    requirements: {
      playerLevel: 3,
      skills: [{ skillId: 'fishing', level: 2 }],
      equipment: [{ equipmentId: 'rod', level: 2 }],
    },
  },
  {
    id: 'lake',
    name: 'Mountain Lake',
    description: 'A deep, cold lake nestled between peaks. Home to elusive trophy fish.',
    type: 'fishing',
    reveals: ['coast'],
    unlocks: null,
    fishModifier: 0.85,
    fishTier: 2,
    bycatchChance: 0.16,
    requirements: {
      playerLevel: 5,
      skills: [{ skillId: 'fishing', level: 3 }],
      equipment: [{ equipmentId: 'rod', level: 3 }, { equipmentId: 'line', level: 2 }],
    },
  },
  {
    id: 'coast',
    name: 'Open Coast',
    description: 'Where the land meets the sea. Waves crash against rocky shores.',
    type: 'fishing',
    reveals: ['deep_sea'],
    unlocks: null,
    fishModifier: 0.78,
    fishTier: 3,
    bycatchChance: 0.12,
    requirements: {
      playerLevel: 8,
      skills: [{ skillId: 'fishing', level: 4 }, { skillId: 'business', level: 2 }],
      equipment: [{ equipmentId: 'rod', level: 4 }, { equipmentId: 'bait', level: 2 }, { equipmentId: 'boat', level: 1 }],
    },
  },
  {
    id: 'deep_sea',
    name: 'Deep Ocean',
    description: 'Miles from shore. The water is dark and deep. Big predators lurk below.',
    type: 'fishing',
    reveals: ['arctic'],
    unlocks: null,
    fishModifier: 0.72,
    fishTier: 4,
    bycatchChance: 0.08,
    requirements: {
      playerLevel: 12,
      skills: [{ skillId: 'fishing', level: 6 }, { skillId: 'business', level: 3 }],
      equipment: [{ equipmentId: 'rod', level: 5 }, { equipmentId: 'bait', level: 3 }, { equipmentId: 'line', level: 3 }, { equipmentId: 'boat', level: 3 }],
    },
  },
  {
    id: 'arctic',
    name: 'Arctic Waters',
    description: 'Frozen seas at the edge of the world. Only the hardiest fish survive here.',
    type: 'fishing',
    reveals: [],
    unlocks: null,
    fishModifier: 0.70,
    fishTier: 5,
    bycatchChance: 0.03,
    requirements: {
      playerLevel: 18,
      skills: [{ skillId: 'fishing', level: 8 }, { skillId: 'luck', level: 4 }],
      equipment: [{ equipmentId: 'rod', level: 7 }, { equipmentId: 'line', level: 5 }, { equipmentId: 'bait', level: 4 }, { equipmentId: 'boat', level: 5 }],
    },
  },
];

export function getLocationDefById(id: string): LocationDef | undefined {
  return LOCATION_DEFS.find(l => l.id === id);
}

export function isLocationUnlocked(
  locationDef: LocationDef,
  state: { totalCoinsEarned: number; fishCaught: number; discoveredLocations: string[]; playerStats: PlayerStats; equipment: EquipmentInstance[]; properties: PropertyInstance[] }
): boolean {
  const req = locationDef.requirements;

  if (req.playerLevel !== undefined && req.playerLevel > 0) {
    if (state.playerStats.level < req.playerLevel) return false;
  }

  if (req.skills) {
    for (const skillReq of req.skills) {
      const skill = state.playerStats.skills[skillReq.skillId as keyof typeof state.playerStats.skills];
      if (!skill || skill.level < skillReq.level) return false;
    }
  }

  if (req.equipment) {
    for (const equipReq of req.equipment) {
      const equip = state.equipment.find(e => e.id === equipReq.equipmentId);
      if (!equip || equip.level < equipReq.level) return false;
    }
  }

  if (req.properties) {
    for (const propReq of req.properties) {
      const prop = state.properties.find(p => p.id === propReq.propertyId);
      if (!prop || prop.tier < propReq.tier) return false;
    }
  }

  if (req.npcQuest) {
    return false;
  }

  return true;
}

export function getUnmetRequirements(
  locationDef: LocationDef,
  state: { playerStats: PlayerStats; equipment: EquipmentInstance[]; properties: PropertyInstance[] }
): string[] {
  const unmet: string[] = [];
  const req = locationDef.requirements;

  if (req.playerLevel !== undefined && req.playerLevel > 0) {
    if (state.playerStats.level < req.playerLevel) {
      unmet.push(`Player Lv.${req.playerLevel} (you: Lv.${state.playerStats.level})`);
    }
  }

  if (req.skills) {
    for (const skillReq of req.skills) {
      const skill = state.playerStats.skills[skillReq.skillId as keyof typeof state.playerStats.skills];
      const currentLevel = skill?.level ?? 0;
      if (currentLevel < skillReq.level) {
        const name = skillReq.skillId.charAt(0).toUpperCase() + skillReq.skillId.slice(1);
        unmet.push(`${name} Lv.${skillReq.level} (you: Lv.${currentLevel})`);
      }
    }
  }

  if (req.equipment) {
    for (const equipReq of req.equipment) {
      const equip = state.equipment.find(e => e.id === equipReq.equipmentId);
      const currentLevel = equip?.level ?? 0;
      if (currentLevel < equipReq.level) {
        if (equipReq.equipmentId === 'boat') {
          unmet.push(`${getBoatTierName(equipReq.level)} (you: ${getBoatTierName(currentLevel) || 'None'})`);
        } else {
          const name = equipReq.equipmentId.charAt(0).toUpperCase() + equipReq.equipmentId.slice(1);
          unmet.push(`${name} Lv.${equipReq.level} (you: Lv.${currentLevel})`);
        }
      }
    }
  }

  if (req.properties) {
    for (const propReq of req.properties) {
      const prop = state.properties.find(p => p.id === propReq.propertyId);
      const currentTier = prop?.tier ?? 0;
      if (currentTier < propReq.tier) {
        const name = propReq.propertyId.charAt(0).toUpperCase() + propReq.propertyId.slice(1);
        unmet.push(`${name} Tier ${propReq.tier} (you: Tier ${currentTier})`);
      }
    }
  }

  return unmet;
}
